import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@altimateai/lego";
import "./index.css";
import { EventContent } from "./EventContent";
import { ScheduleCard } from "./types";
import { EventTypeInfo, ScheduleStatus } from "./constants";
import { EventClickArg } from "@fullcalendar/core";
import { approveRecommendations } from "@/helpers/apis";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { GET_WAREHOUSE_SCHEDULED_EVENTS } from "../../constants";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import Header from "./Header";
import { toast } from "react-toastify";
import { ViewScheduleContent } from "./ViewScheduleContent";
import classNames from "classnames";
import styles from "./styles.module.scss";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Sheet,
  SheetClose,
  SheetContent,
  Spinner,
  Stack,
} from "@altimateai/lego";
import { getIcon } from "@/pages/warehouse/components/scheduler/helper";
import { IconChevronLeft, IconChevronRight, IconX } from "@tabler/icons-react";
import { WarehouseTracking } from "@/lib/tracking/warehouse";

dayjs.extend(isBetween);

interface ViewState {
  activeStart: Date;
  activeEnd: Date;
  viewType: "timeGridWeek" | "timeGridDay";
}

interface SchedulerProps {
  warehouseName: string;
  instanceId: string;
  cards: ScheduleCard[];
  addScheduleFunction: (params: { startDate?: string; endDate?: string }) => void;
  isSchedulingEnabled?: boolean;
  defaultWarehouseSize?: string;
  isDynamicSchedule?: boolean;
  onCalenderDatesChange: (start: Date, end: Date) => void;
  isLoading?: boolean;
  canSchedule?: boolean;
}

export const Scheduler = ({
  warehouseName,
  instanceId,
  cards,
  addScheduleFunction,
  isSchedulingEnabled = false,
  defaultWarehouseSize,
  isDynamicSchedule = false,
  onCalenderDatesChange,
  isLoading = false,
  canSchedule = true,
}: SchedulerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [disableApplySchedule, setDisableApplySchedule] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<ScheduleCard | null>(null);
  const [nextCard, setNextCard] = useState<ScheduleCard | null>(null);
  const [prevCard, setPrevCard] = useState<ScheduleCard | null>(null);
  const [applyScheduleInfo, setApplyScheduleInfo] = useState<{
    startDate: string;
    endDate: string;
    scheduleCount: number;
  } | null>(null);
  const queryClient = useQueryClient();
  const calendarRef = useRef<FullCalendar>(null);
  const [view, setView] = useState<ViewState>({
    activeStart: new Date(),
    activeEnd: new Date(),
    viewType: "timeGridWeek",
  });
  const [applyButtonEl, setApplyButtonEl] = useState<HTMLElement | null>(null);
  const [customSchedulerButtons, setCustomSchedulerButtons] = useState({});
  const [customButtonsHeader, setCustomButtonsHeader] = useState("");

  const isViewInPast = useCallback(() => {
    const now = dayjs();
    return dayjs(view.activeEnd).isBefore(now);
  }, [view.activeEnd]);

  useEffect(() => {
    // Wait for next tick to ensure calendar is mounted
    setTimeout(() => {
      const applyButton = document.querySelector(".fc-applyScheduleButton-button") as HTMLElement;
      if (applyButton) {
        const shouldDisable = !isSchedulingEnabled || isViewInPast() || disableApplySchedule;
        if (shouldDisable) {
          applyButton.classList.add("fc-button-disabled");
          applyButton.style.opacity = "0.65";
          applyButton.style.cursor = "not-allowed";
          applyButton.setAttribute("aria-disabled", "true");
          applyButton.setAttribute("disabled", "true");
        } else {
          applyButton.classList.remove("fc-button-disabled");
          applyButton.style.opacity = "";
          applyButton.style.cursor = "";
          applyButton.removeAttribute("aria-disabled");
          applyButton.removeAttribute("disabled");
        }
      }
    }, 0);
  }, [isSchedulingEnabled, view.activeEnd, disableApplySchedule]);

  useEffect(() => {
    if (getUnappliedScheduleCount(view.activeStart.toString(), view.activeEnd.toString()) === 0) {
      setDisableApplySchedule(true);
    } else {
      setDisableApplySchedule(false);
    }
  }, [cards, view]);

  useEffect(() => {
    if (!canSchedule) {
      setCustomSchedulerButtons({});
      setCustomButtonsHeader("");
      return;
    }
    if (!isDynamicSchedule) {
      setCustomSchedulerButtons({
        addScheduleButton: {
          text: "Create Schedule Block",
          click: () => addScheduleFunction({ startDate: "", endDate: "" }),
        },
        applyScheduleButton: {
          text: getApplyButtonText(),
          click: (ev: MouseEvent) => {
            const buttonEl = ev.target as HTMLElement;

            const calendarApi = calendarRef.current?.getApi();
            if (!calendarApi) return;

            const view = calendarApi.view;
            const startDate = view.activeStart.toISOString();
            const endDate = view.activeEnd.toISOString();
            const applicableSchedules = getUnappliedScheduleCount(startDate, endDate);

            if (applicableSchedules === 0) {
              return;
            }

            setApplyButtonEl(buttonEl);

            handleApplySchedule(startDate, endDate);
          },
        },
      });
      setCustomButtonsHeader("addScheduleButton applyScheduleButton");
    } else {
      setCustomSchedulerButtons({
        addScheduleButton: {
          text: "Create Schedule Block",
          click: () => addScheduleFunction({ startDate: "", endDate: "" }),
        },
      });
      setCustomButtonsHeader("addScheduleButton");
    }
  }, [isDynamicSchedule, view, cards, disableApplySchedule, canSchedule]);

  const getApplyButtonText = () => {
    return `Apply ${view.viewType === "timeGridWeek" ? "Weekly" : "Daily"} Schedule`;
  };

  const mutation = useMutation({
    mutationFn: ({ startDate, endDate }: { startDate: string; endDate: string }) =>
      approveRecommendations({
        warehouse_name: warehouseName,
        instance_id: instanceId,
        start_time: startDate,
        end_time: endDate,
      }),
    onMutate: () => {
      if (applyButtonEl) {
        applyButtonEl.innerHTML = "Applying...";
        applyButtonEl.setAttribute("disabled", "true");
      }
      toast.loading("Applying scheduled events...", { toastId: "apply-schedule" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GET_WAREHOUSE_SCHEDULED_EVENTS] });
      toast.update("apply-schedule", {
        render: "Scheduled events applied successfully",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
      if (applyButtonEl) {
        applyButtonEl.innerHTML = "";
        applyButtonEl.removeAttribute("disabled");
      }
    },
    onError: error => {
      console.error("Failed to approve schedules:", error);
      toast.update("apply-schedule", {
        render: "Failed to apply scheduled events",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
      if (applyButtonEl) {
        applyButtonEl.innerHTML = "";
        applyButtonEl.removeAttribute("disabled");
      }
    },
  });

  const toggleModal = () => {
    setIsOpen(!isOpen);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const getClassNameForEvent = useCallback((event: ScheduleCard) => {
    if (event.type === "default") return "card-default-schedule";

    const isExpired = dayjs(event.end).isBefore(dayjs());

    if (isExpired) {
      if (event.status === ScheduleStatus.Approved && event.type !== "block") {
        return `card-approved-expired`;
      }
      return `card-expired`;
    }

    switch (event.type) {
      case EventTypeInfo.recommended.key:
        if (event.status === ScheduleStatus.Approved) {
          return `card-approved`;
        }
        if (event.status === ScheduleStatus.Rejected) {
          return `card-rejected`;
        }
        return `card-default`;
      case EventTypeInfo.custom.key:
        if (event.status === ScheduleStatus.Approved) {
          return `card-custom-applied`;
        }
        return `card-custom`;
      default:
        return `card-${event.type}`;
    }
  }, []);

  const getTextColorForEvent = useCallback((event: ScheduleCard) => {
    switch (event.type) {
      case EventTypeInfo.recommended.key:
        if (event.status === ScheduleStatus.Rejected) {
          return EventTypeInfo.deleted.textColor;
        }

        if (event.status === ScheduleStatus.Pending) {
          return EventTypeInfo.default.textColor;
        }

        return EventTypeInfo.recommended.textColor;

      case EventTypeInfo.custom.key:
        if (event.status === ScheduleStatus.Approved) {
          return EventTypeInfo.approved.textColor;
        }
        return EventTypeInfo.default.textColor;

      default:
        return EventTypeInfo[event.type].textColor;
    }
  }, []);

  const events = useMemo(
    () =>
      cards.map(card => ({
        extendedProps: card,
        className: getClassNameForEvent(card),
        textColor: getTextColorForEvent(card),
        ...card,
      })),
    [cards, getClassNameForEvent, getTextColorForEvent]
  );

  const getUnappliedScheduleCount = (startDate: string, endDate: string) => {
    return cards.filter(card => {
      const isUnapplied = card.status !== ScheduleStatus.Approved;
      const isNotExpired = dayjs(card.end).isAfter(dayjs());

      // Check if the schedule overlaps with the date range in any way
      const cardStart = dayjs(card.start);
      const cardEnd = dayjs(card.end);
      const viewStart = dayjs(startDate);
      const viewEnd = dayjs(endDate);

      // A schedule overlaps with the range if:
      // 1. The start date is within the range, OR
      // 2. The end date is within the range, OR
      // 3. The schedule spans the entire range (starts before and ends after)
      const isInRange =
        cardStart.isBetween(viewStart, viewEnd, null, "[]") ||
        cardEnd.isBetween(viewStart, viewEnd, null, "[]") ||
        (cardStart.isBefore(viewStart) && cardEnd.isAfter(viewEnd));

      const isNotBlock = card.type !== "block" && card.type !== "default";
      return isUnapplied && isNotExpired && isInRange && isNotBlock;
    }).length;
  };

  const handleApplySchedule = (startDate: string, endDate: string) => {
    const scheduleCount = getUnappliedScheduleCount(startDate, endDate);
    setApplyScheduleInfo({ startDate, endDate, scheduleCount });
    setIsConfirmModalOpen(true);
  };

  const confirmApplySchedule = () => {
    if (!applyScheduleInfo) return;

    WarehouseTracking.schedulesApproved(warehouseName, applyScheduleInfo.scheduleCount);
    mutation.mutate({ startDate: applyScheduleInfo.startDate, endDate: applyScheduleInfo.endDate });
    setIsConfirmModalOpen(false);
  };

  useEffect(() => {
    const currentIndex = cards.findIndex(card => card.id === selectedCard?.id);
    if (currentIndex !== -1) {
      // Find the previous card that is not of type "block"
      const prevIndex = cards
        .slice(0, currentIndex)
        .reverse()
        .findIndex(card => card.type !== "block" && card.type !== "default");
      setPrevCard(prevIndex !== -1 ? cards[currentIndex - prevIndex - 1] : null);

      // Find the next card that is not of type "block" or "default"
      const nextIndex = cards
        .slice(currentIndex + 1)
        .findIndex(card => card.type !== "block" && card.type !== "default");
      setNextCard(nextIndex !== -1 ? cards[currentIndex + nextIndex + 1] : null);
    }
  }, [selectedCard, cards]);

  const handleEventClick = (info: EventClickArg) => {
    const card = info.event.extendedProps as ScheduleCard;
    if (card.type === "block" || card.type === "default") {
      return;
    }
    setSelectedCard(card);
    if (card.type === "auto_resize" || card.type === "backoff") {
      setIsSheetOpen(true);
    } else {
      toggleModal();
    }
  };

  const handleCardChange = (direction: "next" | "prev") => {
    if (direction === "next") {
      const nextIndex = cards.findIndex(card => card.id === nextCard?.id);
      const nextValidCard = cards
        .slice(nextIndex + 1)
        .find(card => card.type !== "block" && card.type !== "default");
      setSelectedCard(nextValidCard || nextCard);
    }
    if (direction === "prev") {
      const prevIndex = cards.findIndex(card => card.id === prevCard?.id);
      const prevValidCard = cards
        .slice(0, prevIndex)
        .reverse()
        .find(card => card.type !== "block" && card.type !== "default");
      setSelectedCard(prevValidCard || prevCard);
    }
  };

  const renderEventContent = (eventInfo: { event: { extendedProps: ScheduleCard } }) => {
    const event = eventInfo.event.extendedProps as ScheduleCard;
    return <EventContent event={event} />;
  };

  const handleSelect = (info: { startStr: string; endStr: string }) => {
    const now = dayjs();
    const startDate = dayjs(info.startStr);
    const endDate = dayjs(info.endStr);

    // Check if either the selected start date or end date is in the past
    if (startDate.isBefore(now) || endDate.isBefore(now)) {
      toast.error("Cannot create a schedule block with a start or end time in the past.");
      return;
    }

    // Check for overlapping schedule blocks
    const overlappingCard = cards.find(card => {
      if (card.type === "default") return false;
      const cardStart = dayjs(card.start);
      const cardEnd = dayjs(card.end);
      return startDate.isBefore(cardEnd) && endDate.isAfter(cardStart);
    });

    if (overlappingCard) {
      toast.error(
        `This schedule block overlaps with existing schedule block - ${overlappingCard.type}: ${dayjs(overlappingCard.start).format("h:mm a")} - ${dayjs(overlappingCard.end).format("h:mm a")}. Please delete that schedule block first.`
      );
      return;
    }

    addScheduleFunction({ startDate: info.startStr, endDate: info.endStr });
  };

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      // Instead of re-rendering, just refetch events
      calendarApi.refetchEvents();
    }
  }, [events]);

  return (
    <Stack direction="column">
      {!isFullscreen && (
        <Header
          isSchedulingEnabled={isSchedulingEnabled}
          onToggleFullscreen={toggleFullscreen}
          isFullscreen={isFullscreen}
        />
      )}
      <div className={classNames({ [styles.fullscreenCalendar]: isFullscreen })}>
        {isFullscreen && (
          <Header
            isSchedulingEnabled={isSchedulingEnabled}
            onToggleFullscreen={toggleFullscreen}
            isFullscreen={isFullscreen}
          />
        )}
        <div aria-label={"calendar"} className={"schedule-calendar al-relative"}>
          <FullCalendar
            // @ts-expect-error valid
            loading={isLoading}
            nowIndicator={true}
            nowIndicatorClassNames={"al-text-destructive !al-border-1.5"}
            plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
            initialView="timeGridWeek"
            slotDuration="00:15:00"
            snapDuration="00:15:00"
            events={events}
            eventClick={handleEventClick}
            eventContent={renderEventContent}
            allDaySlot={false}
            lazyFetching={true}
            headerToolbar={{
              left: "timeGridWeek,timeGridDay prev,next title",
              right: customButtonsHeader,
            }}
            customButtons={customSchedulerButtons}
            select={canSchedule ? handleSelect : undefined}
            selectable={canSchedule}
            ref={calendarRef}
            datesSet={dateInfo => {
              onCalenderDatesChange(dateInfo.start, dateInfo.end);
              setView({
                activeStart: dateInfo.start,
                activeEnd: dateInfo.end,
                viewType: dateInfo.view.type as "timeGridWeek" | "timeGridDay",
              });
            }}
          />
          {isLoading && (
            <Stack
              justify={"center"}
              align={"center"}
              className={
                "al-absolute al-top-20 al-left-11 al-bottom-0 al-right-0 al-bg-gray-300 al-opacity-50 al-z-10"
              }
            >
              <Spinner size={"lg"} />
              Loading Events...
            </Stack>
          )}
        </div>
      </div>
      <Dialog open={isOpen} onOpenChange={open => !open && toggleModal()}>
        <DialogContent className="max-w-3xl">
          <ViewScheduleContent
            instanceId={instanceId}
            event={selectedCard!}
            onClose={toggleModal}
            warehouseName={warehouseName}
            defaultWarehouseSize={defaultWarehouseSize}
          />
        </DialogContent>
      </Dialog>
      <Dialog
        open={isConfirmModalOpen}
        onOpenChange={open => !open && setIsConfirmModalOpen(false)}
      >
        <DialogContent className={styles.confirmationModal}>
          <DialogHeader>
            <DialogTitle>Schedule Confirmation</DialogTitle>
          </DialogHeader>
          <div className={styles.confirmationContent}>
            <p>
              Are you sure you want to apply all{" "}
              <strong>{applyScheduleInfo?.scheduleCount} schedules</strong> from{" "}
              <strong>
                {dayjs(applyScheduleInfo?.startDate).format("MMMM D")} to{" "}
                {dayjs(applyScheduleInfo?.endDate).subtract(1, "day").format("MMMM D")}
              </strong>
              ?
            </p>
            <div className={styles.confirmationActions}>
              <button
                className={classNames(styles.button, styles.cancelButton)}
                onClick={() => setIsConfirmModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className={classNames(styles.button, styles.confirmButton)}
                onClick={confirmApplySchedule}
              >
                Yes, Apply {view.viewType === "timeGridWeek" ? "weekly" : "daily"} schedule
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent
          className="!al-w-1/2 !al-max-w-[700px] [&>*:nth-child(2)]:al-hidden !al-p-0"
          aria-label="Event details sheet"
        >
          <Stack direction={"column"} className={"al-min-h-full"}>
            <Stack
              className={"al-w-full al-px-6 al-py-2 al-bg-action-soft-brand"}
              justify={"between"}
              align={"center"}
              direction="row"
            >
              <Stack align={"center"} gap={"md"}>
                <span className={"al-text-xl al-font-semibold"}>Decision</span>
                {selectedCard &&
                  getIcon({
                    type: selectedCard.type,
                    change: selectedCard.change,
                  })}
              </Stack>
              <Stack align={"center"} gap={"md"}>
                <Button
                  variant={"secondary"}
                  size={"sm"}
                  onClick={() => handleCardChange("prev")}
                  disabled={!prevCard}
                  aria-label="Previous event"
                >
                  <IconChevronLeft />
                </Button>
                <Button
                  variant={"default"}
                  size={"sm"}
                  onClick={() => handleCardChange("next")}
                  disabled={!nextCard}
                  aria-label="Next event"
                >
                  <IconChevronRight />
                </Button>
                <SheetClose asChild>
                  <Button variant={"ghost"}>
                    <IconX />
                  </Button>
                </SheetClose>
              </Stack>
            </Stack>
            <Stack
              className={"al-mt-3 al-min-h-full al-w-full al-px-6 al-grow"}
              direction="row"
              gap="lg"
            >
              <Stack className={"al-w-1/4 al-min-h-full al-border-r "} direction="column" gap="lg">
                <Stack direction="column" gap="sm">
                  <Stack justify={"start"} direction="column" gap="xs">
                    <div className={"al-w-full al-font-bold"}>
                      <span>Schedule Time</span>
                    </div>
                    <div className={"al-font-light"}>
                      <span className={"al-text-action-orange"}>
                        {dayjs(selectedCard?.start).format("h:mm A")} -{" "}
                        {dayjs(selectedCard?.end).format("h:mm A")}
                      </span>
                    </div>
                  </Stack>
                  <Stack justify={"start"} direction="column" gap="xs">
                    <div className={"al-w-full al-font-bold"}>
                      <span>Warehouse Size</span>
                    </div>
                    <div className={"al-font-light"}>
                      <span className={"al-text-brand"}>
                        {selectedCard?.sizeTo || selectedCard?.sizeFrom || "N/A"}
                      </span>
                    </div>
                  </Stack>
                </Stack>
              </Stack>

              <Stack
                justify={"start"}
                align={"start"}
                gap={"lg"}
                direction="column"
                className="al-pl-4 al-w-3/4"
              >
                <div className={"al-w-full al-text-xl al-font-bold"}>
                  <span>Timeline</span>
                </div>
                {selectedCard?.reasoning && selectedCard.reasoning.length > 0 ? (
                  <div className="al-w-full al-overflow-auto">
                    <Accordion
                      type="single"
                      collapsible
                      className={"al-w-full al-flex al-flex-col"}
                    >
                      {(selectedCard.reasoning as { timestamp: string; reason: string }[]).map(
                        (reasoning, index, arr) => {
                          return (
                            <div
                              className={
                                "al-flex al-flex-row-reverse al-items-start al-gap-2 al-pb-3 al-relative"
                              }
                              key={reasoning.timestamp}
                            >
                              <AccordionItem
                                value={reasoning.timestamp}
                                className={
                                  "al-peer !al-border data-[state=closed]:al-border-[#F9FAFB] data-[state=open]:al-border-[#E9F2FF] al-rounded-md al-w-full"
                                }
                              >
                                <AccordionTrigger
                                  className={
                                    "al-px-2 !al-py-2 al-rounded-md data-[state=open]:al-rounded-b-none data-[state=closed]:al-bg-[#F9FAFB] data-[state=open]:al-bg-[#E9F2FF] al-decoration-0 hover:al-decoration-0 hover:al-no-underline"
                                  }
                                >
                                  {dayjs(reasoning.timestamp).format("MMM D, h:mm A")}
                                </AccordionTrigger>
                                <AccordionContent className={"*:al-p-2"}>
                                  {reasoning.reason}
                                </AccordionContent>
                              </AccordionItem>
                              <div
                                className={
                                  "al-h-3 al-w-3 rounded al-mt-3 peer-data-[state=closed]:al-bg-action-brand peer-data-[state=open]:al-bg-white peer-data-[state=open]:al-border-2 peer-data-[state=open]:al-border-action-brand"
                                }
                              ></div>
                              {index !== arr.length - 1 && (
                                <div
                                  className={
                                    "al-min-h-full al-w-0.5 peer-data-[state=closed]:al-bg-[#D5DBE4] peer-data-[state=open]:al-bg-action-brand al-absolute al-left-[0.33rem] al-top-[1.5rem]"
                                  }
                                ></div>
                              )}
                            </div>
                          );
                        }
                      )}
                    </Accordion>
                  </div>
                ) : (
                  "N/A"
                )}
              </Stack>
            </Stack>
          </Stack>
        </SheetContent>
      </Sheet>
    </Stack>
  );
};

export default Scheduler;
